import React, { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { useParams } from "react-router-dom";
import Header from "../../layout/Header/Header";
import {
  Button,
  Row,
  Col,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";
import useFullPageLoader from "../../hooks/useFullPageLoader";

function DetalleTicket() {
  const { user } = useContext(AuthContext);
  const { id } = useParams();
  const messagesEndRef = useRef(null);

  const URL_SUPPORT_TICKETS = process.env.REACT_APP_URL_SUPPORT_TICKETS;
  const URL_SUPPORT_AUDIT_LOG = process.env.REACT_APP_URL_SUPPORT_AUDIT_LOG;
  const URL_SUPPORT_PROJECTS = process.env.REACT_APP_URL_SUPPORT_PROJECTS;

  const [ticket, setTicket] = useState(null);
  const [proyecto, setProyecto] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // Modales
  const [modalStatus, setModalStatus] = useState(false);
  const [modalResolve, setModalResolve] = useState(false);
  
  // Estados para cambios
  const [newStatus, setNewStatus] = useState("");
  const [resolutionType, setResolutionType] = useState("");
  const [resolutionSummary, setResolutionSummary] = useState("");

  // Borrador de la IA pendiente de aprobación (Fase 2 del soporte por grupos)
  const [draftTexto, setDraftTexto] = useState("");

  useEffect(() => {
    getTicket();
    getAuditLogs();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [ticket]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const getTicket = () => {
    showLoader();
    axios
      .get(`${URL_SUPPORT_TICKETS}/${id}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setTicket(response.data);
        setDraftTexto(response.data.draft && response.data.draft.status === "pending" ? response.data.draft.text || "" : "");
        getProyecto(response.data.supportProjectId);
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  };

  const getProyecto = (projectId) => {
    axios
      .get(`${URL_SUPPORT_PROJECTS}/${projectId}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setProyecto(response.data);
      })
      .catch((err) => console.log(err));
  };

  const getAuditLogs = () => {
    axios
      .get(`${URL_SUPPORT_AUDIT_LOG}Ticket/${id}`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((response) => {
        setAuditLogs(response.data);
      })
      .catch((err) => console.log(err));
  };

  // Aprueba (ok), edita y envía (editar) o descarta (no) el borrador. Nada sale al grupo sin esto.
  const decidirBorrador = (accion) => {
    const enviar = accion !== "no";
    Swal.fire({
      title: enviar ? "¿Enviar al grupo del cliente?" : "¿Descartar el borrador?",
      text: enviar ? draftTexto : "",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: enviar ? "Sí, enviar" : "Sí, descartar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (!result.isConfirmed) return;
      axios
        .post(
          `${URL_SUPPORT_TICKETS}/${id}/draft`,
          accion === "editar" ? { accion, texto: draftTexto.trim() } : { accion },
          { headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` } }
        )
        .then(() => {
          Swal.fire("Listo", enviar ? "Mensaje enviado al grupo" : "Borrador descartado", "success");
          getTicket();
        })
        .catch((err) => {
          console.log(err);
          const motivo = err.response && err.response.data && err.response.data.motivo;
          Swal.fire("Error", motivo === "no_encontrado" ? "Ese borrador ya fue resuelto por alguien más" : "No se pudo completar la acción", "error");
          getTicket();
        });
    });
  };

  const updateStatus = () => {
    axios
      .patch(
        `${URL_SUPPORT_TICKETS}Status/${id}`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        Swal.fire("Éxito", "Status actualizado correctamente", "success");
        setModalStatus(false);
        getTicket();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo actualizar el status", "error");
      });
  };

  const takeTicket = () => {
    axios
      .patch(
        `${URL_SUPPORT_TICKETS}Assign/${id}`,
        { assignedTo: user.name || user.email },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then(() => {
        return axios.patch(
          `${URL_SUPPORT_TICKETS}Status/${id}`,
          { status: "team_handling" },
          {
            headers: {
              Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
            },
          }
        );
      })
      .then((response) => {
        Swal.fire("Éxito", "Ticket asignado correctamente", "success");
        getTicket();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo tomar el ticket", "error");
      });
  };

  const returnToAI = () => {
    axios
      .patch(
        `${URL_SUPPORT_TICKETS}Status/${id}`,
        { status: "ai_handling" },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        Swal.fire("Éxito", "Ticket devuelto a la IA", "success");
        getTicket();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo devolver a la IA", "error");
      });
  };

  const resolveTicket = () => {
    axios
      .patch(
        `${URL_SUPPORT_TICKETS}Resolve/${id}`,
        {
          type: resolutionType,
          summary: resolutionSummary,
        },
        {
          headers: {
            Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
          },
        }
      )
      .then((response) => {
        Swal.fire("Éxito", "Ticket resuelto correctamente", "success");
        setModalResolve(false);
        getTicket();
      })
      .catch((err) => {
        console.log(err);
        Swal.fire("Error", "No se pudo resolver el ticket", "error");
      });
  };

  const closeTicket = () => {
    Swal.fire({
      title: '¿Cerrar ticket?',
      text: "El ticket se marcará como cerrado",
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, cerrar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .patch(
            `${URL_SUPPORT_TICKETS}Close/${id}`,
            {},
            {
              headers: {
                Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
              },
            }
          )
          .then((response) => {
            Swal.fire("Éxito", "Ticket cerrado correctamente", "success");
            getTicket();
          })
          .catch((err) => {
            console.log(err);
            Swal.fire("Error", "No se pudo cerrar el ticket", "error");
          });
      }
    });
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      open: "primary",
      ai_handling: "success",
      ai_paused: "warning",
      team_handling: "info",
      waiting_client: "secondary",
      resolved: "secondary",
      closed: "dark",
    };
    return colors[status] || "secondary";
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      low: "secondary",
      medium: "info",
      high: "warning",
      critical: "danger",
    };
    return colors[priority] || "secondary";
  };

  const getCategoryBadgeColor = (category) => {
    const colors = {
      bug: "danger",
      feature: "success",
      question: "info",
      data_fix: "warning",
      access: "primary",
      urgent: "danger",
      other: "secondary",
    };
    return colors[category] || "secondary";
  };

  const getActionLabel = (action) => {
    const labels = {
      query_db: "Consultó DB",
      read_code: "Leyó código",
      search_code: "Buscó en código",
      create_pr: "Creó PR",
      fix_data: "Corrigió datos",
      check_logs: "Revisó logs",
      escalate: "Escaló",
      respond: "Respondió",
    };
    return labels[action] || action;
  };

  if (!ticket) {
    return (
      <>
        <Header />
        {loader}
      </>
    );
  }

  return (
    <>
      <Header />
      {loader}
      {user.menu_soporte ? (
        <div className="container-fluid">
          <br />
          <Row>
            {/* Columna Principal - Chat */}
            <Col md={8}>
              <Card style={{ height: "80vh", display: "flex", flexDirection: "column" }}>
                <CardHeader>
                  <Row>
                    <Col md={6}>
                      <h5>Conversación</h5>
                    </Col>
                    <Col md={6} align="right">
                      <Button
                        color="secondary"
                        size="sm"
                        href="/ListadoTickets"
                      >
                        <i className="fas fa-arrow-left"></i> Volver
                      </Button>
                    </Col>
                  </Row>
                </CardHeader>
                <CardBody style={{ 
                  flex: 1, 
                  overflowY: "auto", 
                  backgroundColor: "#f5f5f5",
                  padding: "20px"
                }}>
                  {ticket.messages && ticket.messages.length > 0 ? (
                    ticket.messages.map((message, index) => (
                      <div
                        key={index}
                        style={{
                          marginBottom: "15px",
                          display: "flex",
                          justifyContent: message.from === "client" ? "flex-start" : "flex-end",
                        }}
                      >
                        <div
                          style={{
                            maxWidth: "70%",
                            padding: "10px 15px",
                            borderRadius: "10px",
                            backgroundColor: 
                              message.from === "client" ? "#e9ecef" :
                              message.from === "ai" ? "#d4edda" : "#cfe2ff",
                            opacity: message.isCommand ? 0.5 : 1,
                            fontSize: message.isCommand ? "0.85rem" : "1rem",
                          }}
                        >
                          <div style={{ marginBottom: "5px" }}>
                            <strong>
                              {message.senderName || message.from}
                            </strong>
                            {message.from === "ai" && (
                              <Badge color="success" className="ml-2" size="sm">IA</Badge>
                            )}
                            {message.from === "team" && (
                              <Badge color="primary" className="ml-2" size="sm">Equipo</Badge>
                            )}
                            {message.isCommand && (
                              <Badge color="secondary" className="ml-2" size="sm">Comando</Badge>
                            )}
                          </div>
                          <div>{message.content}</div>
                          <div style={{ 
                            fontSize: "0.75rem", 
                            color: "#6c757d", 
                            marginTop: "5px",
                            textAlign: "right"
                          }}>
                            {moment(message.timestamp).format("HH:mm")}
                          </div>
                          {message.aiMetadata && (
                            <div style={{
                              marginTop: "10px",
                              fontSize: "0.8rem",
                              borderTop: "1px solid #ccc",
                              paddingTop: "5px"
                            }}>
                              <small>
                                <strong>Confianza:</strong> {message.aiMetadata.confidence}%
                                {message.aiMetadata.toolsUsed && message.aiMetadata.toolsUsed.length > 0 && (
                                  <>
                                    <br />
                                    <strong>Tools:</strong> {message.aiMetadata.toolsUsed.join(", ")}
                                  </>
                                )}
                              </small>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-muted">No hay mensajes en este ticket</p>
                  )}
                  <div ref={messagesEndRef} />
                </CardBody>
              </Card>
            </Col>

            {/* Columna Lateral - Información */}
            <Col md={4}>
              {/* Borrador de la IA pendiente de aprobación */}
              {ticket.draft && ticket.draft.status === "pending" && (
                <Card className="mb-3" style={{ borderColor: "#17a2b8" }}>
                  <CardHeader>
                    <h6>
                      Borrador de la IA <Badge color="info">{ticket.draft.code}</Badge>
                    </h6>
                  </CardHeader>
                  <CardBody>
                    {ticket.draft.needsHuman && (
                      <p className="text-warning mb-2">La IA recomienda revisión humana antes de enviar.</p>
                    )}
                    {ticket.draft.aiMetadata && ticket.draft.aiMetadata.reasoning && (
                      <small className="text-muted d-block mb-2">
                        {ticket.draft.aiMetadata.reasoning} (confianza {Math.round((ticket.draft.aiMetadata.confidence || 0) * 100)}%)
                      </small>
                    )}
                    {ticket.draft.technicalNotes && (
                      <div className="mb-2 p-2" style={{ background: "#f6f8fa", borderRadius: "4px", fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                        <strong>Notas técnicas (solo equipo)</strong>
                        <br />
                        {ticket.draft.technicalNotes}
                      </div>
                    )}
                    <Input
                      type="textarea"
                      rows={6}
                      value={draftTexto}
                      onChange={(e) => setDraftTexto(e.target.value)}
                      placeholder={ticket.draft.noResponse ? "La IA no propone respuesta; escribe la tuya si quieres contestar." : ""}
                    />
                    <div className="mt-2">
                      <Button
                        color="success"
                        size="sm"
                        disabled={!draftTexto.trim()}
                        onClick={() => decidirBorrador(draftTexto.trim() === (ticket.draft.text || "").trim() ? "ok" : "editar")}
                      >
                        Enviar al grupo
                      </Button>{" "}
                      <Button color="secondary" size="sm" onClick={() => decidirBorrador("no")}>
                        Descartar
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )}

              {/* Panel de Información */}
              <Card className="mb-3">
                <CardHeader>
                  <h6>Información del Ticket</h6>
                </CardHeader>
                <CardBody>
                  <div className="mb-2">
                    <strong>Cliente:</strong>
                    <br />
                    {ticket.clientName}
                  </div>
                  <div className="mb-2">
                    <strong>Proyecto:</strong>
                    <br />
                    {proyecto ? (
                      <a href={`/ProyectosSoporteEdit/${proyecto._id}`}>
                        {proyecto.clientName}
                      </a>
                    ) : (
                      "Cargando..."
                    )}
                  </div>
                  <div className="mb-2">
                    <strong>Status:</strong>
                    <br />
                    <Badge color={getStatusBadgeColor(ticket.status)}>
                      {ticket.status}
                    </Badge>{" "}
                    <Button
                      color="link"
                      size="sm"
                      onClick={() => {
                        setNewStatus(ticket.status);
                        setModalStatus(true);
                      }}
                    >
                      <i className="fas fa-edit"></i>
                    </Button>
                  </div>
                  <div className="mb-2">
                    <strong>Prioridad:</strong>
                    <br />
                    <Badge color={getPriorityBadgeColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                  </div>
                  {ticket.category && (
                    <div className="mb-2">
                      <strong>Categoría:</strong>
                      <br />
                      <Badge color={getCategoryBadgeColor(ticket.category)}>
                        {ticket.category}
                      </Badge>
                    </div>
                  )}
                  {ticket.handledBy && (
                    <div className="mb-2">
                      <strong>Atendido por:</strong>
                      <br />
                      <Badge color={
                        ticket.handledBy === 'ai' ? 'success' :
                        ticket.handledBy === 'team' ? 'primary' : 'info'
                      }>
                        {ticket.handledBy === 'ai' ? 'IA' :
                         ticket.handledBy === 'team' ? 'Equipo' : 'Mixto'}
                      </Badge>
                    </div>
                  )}
                  {ticket.assignedTo && (
                    <div className="mb-2">
                      <strong>Asignado a:</strong>
                      <br />
                      {ticket.assignedTo}
                    </div>
                  )}
                  <div className="mb-2">
                    <strong>Creado:</strong>
                    <br />
                    <small>{moment(ticket.createdAt).format("DD/MM/YYYY HH:mm")}</small>
                    <br />
                    <small className="text-muted">{moment(ticket.createdAt).fromNow()}</small>
                  </div>
                  {ticket.firstResponseAt && (
                    <div className="mb-2">
                      <strong>Primera respuesta:</strong>
                      <br />
                      <small>{moment(ticket.firstResponseAt).format("DD/MM/YYYY HH:mm")}</small>
                    </div>
                  )}
                  {ticket.resolvedAt && (
                    <div className="mb-2">
                      <strong>Resuelto:</strong>
                      <br />
                      <small>{moment(ticket.resolvedAt).format("DD/MM/YYYY HH:mm")}</small>
                    </div>
                  )}
                </CardBody>
              </Card>

              {/* Panel de Acciones */}
              <Card className="mb-3">
                <CardHeader>
                  <h6>Acciones</h6>
                </CardHeader>
                <CardBody>
                  <div className="d-grid gap-2">
                    {ticket.status !== "team_handling" && ticket.status !== "resolved" && ticket.status !== "closed" && (
                      <Button color="primary" onClick={takeTicket} block>
                        <i className="fas fa-hand-paper"></i> Tomar Ticket
                      </Button>
                    )}
                    {ticket.status === "team_handling" && proyecto?.aiConfig?.enabled && (
                      <Button color="info" onClick={returnToAI} block>
                        <i className="fas fa-robot"></i> Devolver a IA
                      </Button>
                    )}
                    {ticket.status !== "resolved" && ticket.status !== "closed" && (
                      <Button color="success" onClick={() => setModalResolve(true)} block>
                        <i className="fas fa-check"></i> Resolver
                      </Button>
                    )}
                    {ticket.status === "resolved" && ticket.status !== "closed" && (
                      <Button color="dark" onClick={closeTicket} block>
                        <i className="fas fa-times-circle"></i> Cerrar
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>

              {/* Panel de Logs de IA */}
              {auditLogs.length > 0 && (
                <Card>
                  <CardHeader>
                    <h6>Acciones de IA</h6>
                  </CardHeader>
                  <CardBody style={{ maxHeight: "300px", overflowY: "auto" }}>
                    {auditLogs.map((log, index) => (
                      <div key={index} className="mb-2" style={{ borderBottom: "1px solid #e9ecef", paddingBottom: "8px" }}>
                        <div>
                          <Badge color={log.success ? "success" : "danger"}>
                            {getActionLabel(log.action)}
                          </Badge>
                        </div>
                        <small className="text-muted">
                          {moment(log.createdAt).format("DD/MM HH:mm")}
                        </small>
                        {log.detail?.prUrl && (
                          <div>
                            <small>
                              <a href={log.detail.prUrl} target="_blank" rel="noopener noreferrer">
                                Ver PR
                              </a>
                            </small>
                          </div>
                        )}
                        {log.detail?.collection && (
                          <div>
                            <small>Colección: {log.detail.collection}</small>
                          </div>
                        )}
                        {log.error && (
                          <div>
                            <small className="text-danger">Error: {log.error}</small>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardBody>
                </Card>
              )}

              {/* Panel de Resolución */}
              {ticket.resolution && (
                <Card className="mt-3">
                  <CardHeader>
                    <h6>Resolución</h6>
                  </CardHeader>
                  <CardBody>
                    <div className="mb-2">
                      <strong>Tipo:</strong>
                      <br />
                      <Badge color="success">{ticket.resolution.type}</Badge>
                    </div>
                    <div className="mb-2">
                      <strong>Resumen:</strong>
                      <br />
                      {ticket.resolution.summary}
                    </div>
                    {ticket.resolution.changes && ticket.resolution.changes.length > 0 && (
                      <div>
                        <strong>Cambios realizados:</strong>
                        <ul>
                          {ticket.resolution.changes.map((change, index) => (
                            <li key={index}>
                              <Badge color="info">{change.type}</Badge> {change.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
        </div>
      ) : undefined}

      {/* Modal cambiar status */}
      <Modal isOpen={modalStatus} toggle={() => setModalStatus(!modalStatus)}>
        <ModalHeader toggle={() => setModalStatus(!modalStatus)}>
          Cambiar Status
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Nuevo Status</Label>
            <Input
              type="select"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <option value="open">Abierto</option>
              <option value="ai_handling">IA Atendiendo</option>
              <option value="ai_paused">IA Pausada</option>
              <option value="team_handling">Equipo Atendiendo</option>
              <option value="waiting_client">Esperando Cliente</option>
              <option value="resolved">Resuelto</option>
              <option value="closed">Cerrado</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={updateStatus}>
            Actualizar
          </Button>{" "}
          <Button color="secondary" onClick={() => setModalStatus(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal resolver ticket */}
      <Modal isOpen={modalResolve} toggle={() => setModalResolve(!modalResolve)}>
        <ModalHeader toggle={() => setModalResolve(!modalResolve)}>
          Resolver Ticket
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Tipo de Resolución</Label>
            <Input
              type="select"
              value={resolutionType}
              onChange={(e) => setResolutionType(e.target.value)}
            >
              <option value="">Seleccione...</option>
              <option value="code_fix">Corrección de código</option>
              <option value="db_fix">Corrección de datos</option>
              <option value="explanation">Explicación</option>
              <option value="config_change">Cambio de configuración</option>
              <option value="escalated">Escalado</option>
            </Input>
          </FormGroup>
          <FormGroup>
            <Label>Resumen de la Resolución</Label>
            <Input
              type="textarea"
              rows="4"
              value={resolutionSummary}
              onChange={(e) => setResolutionSummary(e.target.value)}
              placeholder="Describe cómo se resolvió el problema..."
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="success" onClick={resolveTicket}>
            Resolver
          </Button>{" "}
          <Button color="secondary" onClick={() => setModalResolve(false)}>
            Cancelar
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default DetalleTicket;
